import type { JobDetailsView } from '@seek/job-details-view';
import { useEffect, useMemo, useState, type ComponentProps } from 'react';

import { parseCookies } from 'src/modules/safe-cookies';
import { useSelector } from 'src/store/react';
import { selectFeatureFlag } from 'src/store/selectors';

type CompetitiveJobType = NonNullable<
  ComponentProps<typeof JobDetailsView>['competitiveJobs']
>[number];

const MOCK_COMPETITIVE_JOBS: CompetitiveJobType[] = [
  {
    company: {
      name: 'Kingpin Products',
    },
    title: 'Order Entry / Customer Service',
    id: '81577053',
    location: 'Bayside & South Eastern Suburbs, Melbourne VIC',
    postedDateFormatted: '5 hours ago',
    branding: {
      assets: {
        logo: {
          id: 'abc123',
          url: 'https://bx-branding-gateway.cloud.seek.com.au/da7a623d-ef56-618b-af58-78ee1b84ec63.1/serpLogo',
          strategies: {
            jdpLogo:
              'https://bx-branding-gateway.cloud.seek.com.au/da7a623d-ef56-618b-af58-78ee1b84ec63.1/serpLogo',
          },
        },
      },
    },
  },
  {
    company: {
      name: 'SEEK Limited',
    },
    title: 'Assistant Operations Manager (Squad Lead)',
    id: '82069439',
    location: 'North Sydney, Sydney NSW',
    postedDateFormatted: '2 days ago',
    branding: {
      assets: {
        logo: {
          id: 'abc123',
          url: 'https://bx-branding-gateway.cloud.seek.com.au/f439f542-43b4-64b2-9f6a-024c0eae8293.1/serpLogo',
          strategies: {
            jdpLogo:
              'https://bx-branding-gateway.cloud.seek.com.au/f439f542-43b4-64b2-9f6a-024c0eae8293.1/serpLogo',
          },
        },
      },
    },
  },
  {
    company: {
      name: 'Private Advertiser',
    },
    title: 'Come Work in Tasmania in Sales',
    id: '81451531',
    location: 'Melbourne VIC',
    postedDateFormatted: '1 week ago',
  },
];

export const useMockCompetitiveJobs = (jobId: number) => {
  const enableCompetitivePlacement = useSelector(
    selectFeatureFlag('competitivePlacement'),
  );

  const cookies = useMemo(() => parseCookies(), []);

  const [isReturnMock, setIsReturnMock] = useState(false);

  // To avoid SSR mismatch, since we don't have access to cookies on the server
  useEffect(() => {
    setIsReturnMock(
      enableCompetitivePlacement && cookies.COMPETITIVE_PLACEMENT === 'MOCK',
    );
  }, [cookies.COMPETITIVE_PLACEMENT, enableCompetitivePlacement]);

  return useMemo(
    () =>
      isReturnMock
        ? // When ID end with 0, 1, 2, 3, show 0, 1, 2, 3 competitive jobs respectively
          // Otherwise, use the last digit to determine how many competitive jobs to show by using the remainder of 4
          MOCK_COMPETITIVE_JOBS.sort(() => Math.random() - 0.5).splice(
            0,
            (Number(jobId) % 10) % 4,
          )
        : [],
    [isReturnMock, jobId],
  );
};
